import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { styled } from '@mui/material/styles';
import { Card, CardContent, Chip, Stack, Typography } from '@mui/material';

// ----------------------------------------------------------------------

const CHART_HEIGHT = 240;

const ChartWrapperStyle = styled('div')(({ theme }) => ({
  height: CHART_HEIGHT,
  marginTop: theme.spacing(2),
  '& .apexcharts-canvas svg': { height: CHART_HEIGHT },
  '& .apexcharts-canvas svg,.apexcharts-canvas foreignObject': { overflow: 'visible' },
}));

/**
 * Helper to get local "Month Day" from UTC string
 * Converts UTC string to Browser Local Time
 */
const getLocalDateString = (utcString) => {
  const date = new Date(utcString); 
  return new Intl.DateTimeFormat('en-US', { 
    month: 'short', 
    day: 'numeric' 
  }).format(date);
};

// ----------------------------------------------------------------------

export default function AppCurrentSubject({ charges = [], discharges = [], showChip = false }) {
  
  const { chartData, categories, totalSum } = useMemo(() => {
    let sum = 0;
    
    // 1. Format categories for the X-Axis (e.g., "May 11")
    const categoriesList = charges.map(c => getLocalDateString(c.date));
    
    // 2. Process Charge Data
    const chargeSeries = charges.map(c => {
        const val = Number(c.value?.toFixed(2) || 0);
        sum += val;
        return val;
    });

    // 3. Process Discharge Data (making them negative for the bar chart)
    const dischargeSeries = discharges.map(d => {
        const val = Number((d.value || 0).toFixed(2)) * -1;
        sum += val;
        return val;
    });

    return {
      categories: categoriesList,
      totalSum: sum,
      chartData: [
        { name: 'Charge', data: chargeSeries, color: "#00C853" },
        { name: 'Discharge', data: dischargeSeries, color: "#D32F2F" }
      ]
    };
  }, [charges, discharges]);

  const chartOptions = {
    chart: {
      toolbar: { show: false },
      type: "bar",
      zoom: { enabled: false },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => Math.abs(Math.round(val)), // Show absolute values on bars
      style: { fontSize: "10px" },
      offsetY: -20
    },
    legend: { position: 'top', horizontalAlign: 'right' },
    plotOptions: {
      bar: {
        columnWidth: "50%",
        borderRadius: 3,
        dataLabels: { position: "top" },
      },
    },
    xaxis: {
      type: "category",
      categories: categories, // This now contains ["May 11", "May 12", ...]
      axisBorder: { show: false },
      labels: {
        style: { colors: '#919EAB', fontSize: '12px' }
      }
    },
    yaxis: {
      labels: { 
        formatter: (val) => Math.round(val) 
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => `${Math.abs(val).toFixed(2)} kWh`,
      },
    },
  };

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
          Energy Distribution
        </Typography>
        
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4" component="p" fontWeight="700">
              {totalSum.toFixed(2)} <Typography variant="caption" component="span" sx={{ verticalAlign: 'middle' }}>kWh</Typography>
            </Typography>
            {showChip && <Chip size="small" color="success" label="+15%" />}
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Net Energy (Charge vs Discharge)
          </Typography>
        </Stack>

        <ChartWrapperStyle dir="ltr">
          <ReactApexChart
            type="bar"
            series={chartData}
            options={chartOptions}
            height={CHART_HEIGHT}
          />
        </ChartWrapperStyle>
      </CardContent>
    </Card>
  );
}